import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDialogWege } from "./dialogWege.js";
/**
 * Was die zwei strikten Hüllen über `me-dialog` gemeinsam haben — `Modal` (die Masken) und
 * `Arbeitsdialog` (der vollflächige Editor, Paket „Editor-Arbeitsdialog“, Aufgabe 1).
 *
 * Bis zum 09.10.2026 stand all das allein in `Modal.tsx`. Eine zweite Hülle hätte es
 * abschreiben müssen — und eine Abschrift, die beim nächsten Befund nur an einer Stelle
 * nachgezogen wird, ist genau der Fehler, gegen den die Hausregel „striktes Modalpanel“
 * als EIN Baustein geschrieben wurde. Hier steht deshalb:
 *
 *  - die Wege hinaus (`useStrikteWege`): jedes `me-abbrechen` abgefangen, der Riegel
 *    `abbrechenGesperrt` am Rahmen, natives Schliessen am Riegel vorbei → wieder öffnen und
 *    Abbrechen-Weg (Review Aufgabe 1 der Etappe 3, W1), Öffnen im selben Commit;
 *  - die Fokus-Rückgabe an den Auslöser (Aufgabe 8, Befund 13 an me-design);
 *  - der Kontext `ImModal` und das Portal für Subdialoge (`StrikteEbene`, Review K1/K2).
 *
 * Was je Hülle verschieden ist — Breite, Lage, Art, Slots, die Wahl `seitlich`/`stapel` —
 * bleibt in der Hülle.
 */
/**
 * Steht diese Hülle im Inhalt einer anderen? Dann ist sie ein Subdialog und wird per Portal
 * an `document.body` gezeichnet (Review Aufgabe 1, K1/K2): Die Eltern-Maske bekommt von
 * me-dialog `data-neben`/`data-hinten` (`pointer-events: none`) bzw. am Telefon
 * `data-versteckt` (`display: none`), und beides erbte sich über den Flat Tree in einen
 * Subdialog in ihrem Inhalt — nicht zu treffen, am Telefon unsichtbar bei gesperrter Seite.
 * Masken der obersten Ebene bleiben, wo sie stehen (CSS-Kontext ihres Bereichs).
 *
 * `breitePx` ist die Breite der Eltern-Hülle in Pixeln des Entwurfs; `Modal` entscheidet
 * daran, ob es seitlich daneben passt. Der `Arbeitsdialog` meldet `Infinity`: Neben einem
 * vollflächigen Dialog passt nichts, jeder Subdialog darin wird gestapelt.
 */
export const ImModal = createContext(null);
/** Die Eltern-Hülle, in deren Inhalt diese steht — oder `null` auf oberster Ebene. */
export function useElternHuelle() {
    return useContext(ImModal);
}
/**
 * Der zuletzt mit Maus oder Finger angeklickte bedienbare Knopf/Verweis — der Auslöser, wenn
 * der Browser ihn beim Klick NICHT fokussiert (Safari/WebKit, Befund 13 an me-design). Ein
 * Horcher in der Einfangphase am Dokument, einmal angelegt; er liest nur.
 */
let letzterKlick = null;
let klickHorcherAngelegt = false;
const AUSLOESER = "button, a[href], [role=button], [role=menuitem], [tabindex]";
function klickHorcherAnlegen() {
    if (klickHorcherAngelegt || typeof document === "undefined")
        return;
    klickHorcherAngelegt = true;
    document.addEventListener("click", (ereignis) => {
        const ziel = ereignis.target instanceof Element
            ? ereignis.target.closest(AUSLOESER) : null;
        letzterKlick = ziel === null ? null : { ziel, zeit: Date.now() };
    }, true);
}
/**
 * Wer die Hülle geöffnet hat: das fokussierte Element — oder, wenn der Fokus auf <body>
 * steht, der eben (≤ 1 s) angeklickte Knopf (WebKit fokussiert Knöpfe beim Klick nicht).
 */
function ausloeserErmitteln() {
    if (typeof document === "undefined")
        return null;
    const aktiv = document.activeElement;
    if (aktiv instanceof HTMLElement && aktiv !== document.body)
        return aktiv;
    if (letzterKlick !== null && Date.now() - letzterKlick.zeit <= 1000
        && letzterKlick.ziel.isConnected)
        return letzterKlick.ziel;
    return null;
}
/**
 * Die Wege hinaus einer strikten Hülle. Liefert die zwei Refs, die die Hülle an ihren
 * `Dialog` hängt: `anker` in den Titel (darüber findet `useDialogWege` den Host) und
 * `steuerung` als Ref des Dialogs.
 *
 * Die Arbeitsteilung am Riegel, damit sie nicht geraten werden muss:
 *
 *  - **Der RAHMEN hält die Taste** (und das Kreuz im Kopf): Beide melden `me-abbrechen`,
 *    und hier wird JEDES `me-abbrechen` abgefangen (`preventDefault`) — das Element
 *    schliesst nie von selbst. Geschlossen wird, indem der Aufrufer die Hülle aushängt;
 *    so bleiben React-Zustand und Dialog nie uneins. Ist der Ausgang gesperrt, geht der
 *    Rückruf gar nicht erst los (Fixrunde 1, 05.09.2026: ein Riegel allein am Knopf
 *    liesse die Tastatur daran vorbei).
 *  - **Die FUSSLEISTE hält den Knopf** (`ModalFuss` bzw. die Knöpfe des Aufrufers im Fuss
 *    des Arbeitsdialogs): Ein gesperrter Knopf löst gar nicht erst aus.
 *  - **Der Aufrufer hält seinen eigenen Weg.** Die `abbrechen()`-Funktionen prüfen den
 *    Arbeitszustand weiterhin selbst.
 */
export function useStrikteWege(p) {
    const anker = useRef(null);
    const steuerung = useRef(null);
    useDialogWege(anker, {
        beiAbbrechen: (ereignis) => {
            ereignis.preventDefault();
            if (p.abbrechenGesperrt === true)
                return;
            void p.onAbbrechen();
        },
        /*
         * Review Aufgabe 1, W1: Ein natives `cancel` ohne Nutzeraktivierung (Escape mit Fokus
         * auf <body>, Android-Zurück — CloseWatcher-Regeln) lässt sich nicht verhindern; der
         * Dialog geht dann am Riegel und an der Rückfrage vorbei zu. Die Hülle ist aber noch
         * eingehängt — geschlossen wird bei uns NUR durch Aushängen (dann hängt dieser Horcher
         * schon nicht mehr). Also: sofort wieder öffnen und, wenn nicht gesperrt, den
         * Abbrechen-Weg gehen (mit Rückfrage, wenn etwas geändert ist).
         */
        beiGeschlossen: () => {
            steuerung.current?.oeffnen();
            if (p.abbrechenGesperrt === true)
                return;
            void p.onAbbrechen();
        },
    });
    /*
     * Im selben Commit öffnen, in dem die Hülle eingehängt wird (Layout-Effekt): Das
     * kontrollierte `offen` öffnet erst im späteren Effekt — bis dahin stünde ein
     * eingehängter, aber geschlossener Dialog im Dokument, dessen Felder schon da sind und
     * dessen Rahmen fehlt. `offen` bleibt trotzdem gesetzt; es findet den Dialog dann offen.
     * Nach `useDialogWege`: Die Riegel hängen, bevor der Dialog aufgeht.
     */
    useLayoutEffect(() => { steuerung.current?.oeffnen(); }, []);
    /*
     * Fokus-Rückgabe an den Auslöser (ME-Etappe 3, Aufgabe 8; Befund 13 an me-design): Beim
     * ersten Zeichnen merkt sich die Hülle, wer sie geöffnet hat (`ausloeserErmitteln`), und
     * gibt ihm den Fokus beim Aushängen ausdrücklich zurück — sonst fiele er in WebKit auf
     * <body>, hinter eine offene Eltern-Maske. Nur, wenn der Fokus danach VERLOREN ist
     * (<body>): Hat inzwischen jemand anderes ihn gesetzt — der Sprung zum ersten Fehler nach
     * einer 422 etwa —, bleibt er dort. Und nur, wenn der Auslöser noch im Dokument steht.
     */
    const [ausloeser] = useState(() => { klickHorcherAnlegen(); return ausloeserErmitteln(); });
    useLayoutEffect(() => () => {
        if (ausloeser === null)
            return;
        queueMicrotask(() => {
            const aktiv = document.activeElement;
            if (aktiv !== null && aktiv !== document.body)
                return;
            if (ausloeser.isConnected)
                ausloeser.focus();
        });
    }, [ausloeser]);
    return { anker, steuerung };
}
/**
 * Die Ebene einer strikten Hülle: meldet ihre Breite an Subdialoge in ihrem Inhalt
 * (`ImModal`) und zeichnet sich selbst per Portal an `document.body`, wenn sie ihrerseits
 * in einer Hülle steht.
 */
export function StrikteEbene(p) {
    const ebene = _jsx(ImModal.Provider, { value: { breitePx: p.breitePx }, children: p.children });
    return p.eltern !== null ? createPortal(ebene, document.body) : ebene;
}
//# sourceMappingURL=strikteHuelle.js.map